import { EditingDocument } from "..";
import { DomainRegistry } from "../../../../domain";
import { DateInputMethodKinds, TextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import {
  DateItem,
  instanceOfIUserDefinitionItem,
  IUserDefinitionItem,
  TextBoxItem,
} from "../../../../domain/document/items";
import { LightTable } from "../../../../domain/system/table";

/**
 * ユーザ定義の隠し列の値を採番記号として提供する指定がされていて、なおかつフリー入力などで整合が取れていない状態になっているときの対策
 * DMOのWebEditorと同じ挙動にしている
 * ・CSV内に一致する項目がある：隠し列の値を使う
 * ・CSV内に一致する項目がない：空文字
 */
export class UserDefinitionLinkValueApplicator {
  /**
   * 連鎖更新が必要なのでEditingDocumentが必要
   */
  constructor(private _document: EditingDocument) {
  }
  
  async apply(): Promise<void> {
    for (let itemSetId = 0; itemSetId < this._document.itemSets.length; ++itemSetId) {
      for (const item of this._document.itemSets[itemSetId].items) {
        if (!instanceOfIUserDefinitionItem(item)) { continue; }
        if (!this.shouldApplyLinkValue(item)) { continue; }

        const cloned = item.clone() as IUserDefinitionItem;
        const linkValue = await this.getUserDefinitionLinkValue(cloned);
        cloned.setUserDefinitionLinkValues([linkValue]);
        this._document.updateItem(cloned, {
          itemSetId,
        });
      }
    }
  }

  private shouldApplyLinkValue(item: IUserDefinitionItem): boolean {
    // ユーザ定義かどうか、隠し列の値を採番記号として使うように指定されているかどうか
    if (!item.userDefinitionKey || !item.userDefineLinkActions || item.userDefineLinkActions.length === 0) { return false; }
    if (item instanceof TextBoxItem) {
      // ・ユーザ定義入力ではないとき
      // ・テキストがあるのにリンク値がないとき（フリー入力に切り替えて手入力したのち、ユーザ定義に入力方式を戻したとき）
      return (item.inputMethodKind !== TextBoxInputMethodKinds.UserDefinitionSelect) || (!!item.text() && !item.linkedValue);
    }
    if (item instanceof DateItem) {
      // ・ユーザ定義入力ではないとき
      return (item.inputMethodKind !== DateInputMethodKinds.UserDefinitionSelect);
    }
    return false;
  }

  private async getUserDefinitionLinkValue(item: IUserDefinitionItem): Promise<string> {
    // 日付のユーザ定義のリンク値補完はサポートしない（用途的に存在しないはず）
    if (!(item instanceof TextBoxItem)) { return ""; }

    const repository = DomainRegistry.format.getFormatRepository();
    const table = await repository.getUserDefinitionAsLightTable(item.userDefinitionKey);
    const textMatchedRowIndex = this.getTextMatchedFirstRowIndex(table, item.itemName, item.text());
    if (textMatchedRowIndex < 0) { return ""; }

    const linkedHeaderIndex = this.getLinkedHeaderIndex(table, item);
    if (linkedHeaderIndex < 0) { return ""; }
    
    return table.rows[textMatchedRowIndex].values[linkedHeaderIndex];
  }

  private getTextMatchedFirstRowIndex(table: LightTable, itemName: string, value: string): number {
    // TODO: 空のときも一致判定させる必要あり？
    if (!value) { return -1; }
    const headerIndex = this.getNameMatchedHeaderIndex(table, itemName);
    if (headerIndex < 0) { return -1; }
    for (let rowIndex = 0; rowIndex < table.rows.length; ++rowIndex) {
      const row = table.rows[rowIndex];
      if (row.values[headerIndex] === value) { return rowIndex; }
    }
    return -1;
  }

  private getNameMatchedHeaderIndex(table: LightTable, itemName: string): number {
    for (let headerIndex = 0; headerIndex < table.headers.length; ++headerIndex) {
      const header = table.headers[headerIndex];
      // TODO: isVisible考慮する必要あり？
      if (header.value === itemName) { return headerIndex; }
    }
    return -1;
  }

  private getLinkedHeaderIndex(table: LightTable, item: IUserDefinitionItem): number {
    const linkName = this.getLinkName(item);
    if (!linkName) { return -1; }
    return this.getNameMatchedHeaderIndex(table, linkName);
  }
  
  private getLinkName(item: IUserDefinitionItem): string {
    const linkActions = item.userDefineLinkActions;
    if (!linkActions || linkActions.length === 0) { return null; }
    return linkActions[0].name;
  }
}
